import React from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  Platform,
  KeyboardAvoidingView,
  ActivityIndicator,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';

const BTN_NO_BG = '#B0B0B0';
const BTN_YES_BG = '#d4af37';
const BTN_DESTRUCTIVE_BG = '#c62828';

/**
 * App-standard confirm dialog (gold confirm, grey cancel).
 * Pass typedConfirmWord (e.g. "delete") to require typing that word before OK.
 */
export default function BrandedConfirmModal({
  visible,
  title,
  message,
  confirmLabel = 'Yes',
  cancelLabel = 'No',
  onConfirm,
  onCancel,
  destructive = false,
  typedConfirmWord = null,
  typedValue = '',
  onTypedValueChange,
  typedPlaceholder = 'Type delete to confirm',
  busy = false,
  errorMessage = '',
}) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const requiresTypedConfirm = Boolean(typedConfirmWord);
  const typedOk =
    !requiresTypedConfirm ||
    String(typedValue || '').trim().toLowerCase() === String(typedConfirmWord).trim().toLowerCase();
  const confirmEnabled = typedOk && !busy;

  const webRootDataSet =
    Platform.OS === 'web'
      ? {
          brandedConfirmModalRoot: 'true',
          ...(destructive ? { brandedConfirmDestructive: 'true' } : {}),
          ...(requiresTypedConfirm ? { brandedConfirmTyped: 'true' } : {}),
        }
      : undefined;

  const handleCancel = () => {
    if (busy) return;
    onCancel?.();
  };

  const handleConfirm = () => {
    if (!confirmEnabled) return;
    onConfirm?.();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType={Platform.OS === 'web' ? 'none' : 'fade'}
      onRequestClose={handleCancel}
    >
      <View
        style={styles.modalRoot}
        nativeID="branded-confirm-modal-root"
        {...(webRootDataSet ? { dataSet: webRootDataSet } : {})}
      >
        <Pressable
          style={styles.backdropDismiss}
          onPress={handleCancel}
          accessibilityRole="button"
          accessibilityLabel="Close dialog"
          {...(Platform.OS === 'web'
            ? { dataSet: { modalBackdrop: 'true', brandedConfirmBackdrop: 'true' } }
            : {})}
        />
        <KeyboardAvoidingView
          style={styles.backdropCenter}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          pointerEvents="box-none"
          nativeID="branded-confirm-modal-center"
          {...(Platform.OS === 'web'
            ? { dataSet: { brandedConfirmModalCenter: 'true' } }
            : {})}
        >
          <View
            style={styles.sheet}
            nativeID="branded-confirm-modal-sheet"
            {...(Platform.OS === 'web' ? { dataSet: { brandedConfirmModal: 'true' } } : {})}
          >
            <Text
              style={styles.title}
              {...(Platform.OS === 'web'
                ? { dataSet: { brandedConfirmModalTitle: 'true' } }
                : {})}
            >
              {title}
            </Text>
            {message ? (
              <Text
                style={styles.message}
                {...(Platform.OS === 'web'
                  ? { dataSet: { brandedConfirmModalMessage: 'true' } }
                  : {})}
              >
                {message}
              </Text>
            ) : null}
            {requiresTypedConfirm ? (
              <TextInput
                style={styles.typedInput}
                value={typedValue}
                onChangeText={onTypedValueChange}
                placeholder={typedPlaceholder}
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="off"
                editable={!busy}
                autoFocus={Platform.OS === 'web'}
                nativeID="branded-confirm-typed-input"
                {...(Platform.OS === 'web'
                  ? { dataSet: { brandedConfirmTypedInput: 'true' } }
                  : {})}
              />
            ) : null}
            {errorMessage ? (
              <Text
                style={styles.errorText}
                {...(Platform.OS === 'web'
                  ? { dataSet: { brandedConfirmModalError: 'true' } }
                  : {})}
              >
                {errorMessage}
              </Text>
            ) : null}
            <View style={styles.btnRow}>
              <Pressable
                style={[styles.btn, styles.btnCancel, busy && styles.btnDisabled]}
                onPress={handleCancel}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel={cancelLabel}
                nativeID="branded-confirm-cancel-btn"
                {...(Platform.OS === 'web'
                  ? { dataSet: { brandedConfirmCancelBtn: 'true' } }
                  : {})}
              >
                <Text style={styles.btnTextDark}>{cancelLabel}</Text>
              </Pressable>
              <Pressable
                style={[
                  styles.btn,
                  destructive ? styles.btnConfirmDestructive : styles.btnConfirm,
                  !confirmEnabled && styles.btnDisabled,
                ]}
                onPress={handleConfirm}
                disabled={!confirmEnabled}
                accessibilityRole="button"
                accessibilityLabel={confirmLabel}
                accessibilityState={{ disabled: !confirmEnabled }}
                nativeID="branded-confirm-confirm-btn"
                {...(Platform.OS === 'web'
                  ? {
                      dataSet: {
                        brandedConfirmConfirmBtn: 'true',
                        brandedConfirmConfirmDisabled: !confirmEnabled ? 'true' : 'false',
                      },
                    }
                  : {})}
              >
                {busy ? (
                  <ActivityIndicator color="#000" />
                ) : (
                  <Text style={styles.btnTextDark}>{confirmLabel}</Text>
                )}
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' ? { opacity: 1 } : {}),
    },
    backdropDismiss: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.45)',
      zIndex: 0,
    },
    backdropCenter: {
      flex: 1,
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
      backgroundColor: 'transparent',
      zIndex: 1,
    },
    sheet: {
      width: '100%',
      maxWidth: 400,
      backgroundColor: theme.isDarkMode ? '#1e1e1e' : '#FFFFFF',
      zIndex: 2,
      ...(Platform.OS === 'web' ? { opacity: 1 } : {}),
      borderRadius: 12,
      padding: 22,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }),
    },
    title: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.isDarkMode ? '#FDFD0A' : '#d4af37',
      marginBottom: 10,
      textAlign: 'center',
    },
    message: {
      fontSize: 15,
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      marginBottom: 20,
      textAlign: 'center',
      lineHeight: 22,
    },
    btnRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 12,
    },
    btn: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      maxWidth: 140,
    },
    btnCancel: {
      backgroundColor: BTN_NO_BG,
    },
    btnConfirm: {
      backgroundColor: BTN_YES_BG,
    },
    btnConfirmDestructive: {
      backgroundColor: BTN_DESTRUCTIVE_BG,
    },
    btnDisabled: {
      opacity: 0.45,
    },
    btnTextDark: {
      fontSize: 16,
      fontWeight: '700',
      color: '#000',
      backgroundColor: 'transparent',
    },
    typedInput: {
      borderWidth: 1,
      borderColor: theme.colors.border || '#CCCCCC',
      borderRadius: 8,
      paddingVertical: 10,
      paddingHorizontal: 12,
      fontSize: 16,
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#F5F5F5',
      marginBottom: 16,
    },
    errorText: {
      fontSize: 13,
      color: '#c62828',
      textAlign: 'center',
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
  });
}
